import React, { useEffect, useState } from 'react';
import { Card, message, Table, Typography, Spin, Pagination, Row, Col, Progress, Select, DatePicker, Tooltip, Statistic, Skeleton, Button, InputNumber, Badge, Modal } from 'antd';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { getCreditHistoryApi, getCreditBalanceApi, getCreditRateApi, createCheckoutSessionApi } from '../../../services/credit.service';
import dayjs from 'dayjs';
import { ShoppingCartOutlined, ThunderboltOutlined, RocketOutlined, CrownOutlined, EditOutlined, WalletOutlined, FireOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Icons for packages based on order
const PACKAGE_ICONS = [
  <RocketOutlined />,
  <CrownOutlined />,
  <ThunderboltOutlined />,
];

const UsageDashboard = () => {
  const [creditHistoryTransactions, setCreditHistoryTransactions] = useState([]);
  const [creditRate, setCreditRate] = useState({
    loading: true,
    data: null,
    packages: [],
  });
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    limit: 10,
  });
  const [creditBalance, setCreditBalance] = useState({
    total_credits: 0,
    remaining: 0,
  });
  const [transactionType, setTransactionType] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [transactionCounts, setTransactionCounts] = useState({
    addition: 0,
    usage: 0,
  });
  // Updated loading state to an object
  const [loading, setLoading] = useState({
    balance: false,
    transactions: false,
  });
  
  // Checkout state
  const [checkoutLoading, setCheckoutLoading] = useState(null); // null or package identifier
  const [customUsd, setCustomUsd] = useState(100);
  
  // Payment result dialog state
  const [paymentDialog, setPaymentDialog] = useState({
    visible: false,
    status: null, // 'success' or 'cancel'
  });

  const dispatch = useAppDispatch();

  const usedCredits = creditBalance.total_credits - creditBalance.remaining;
  const remainingPercentage = creditBalance.total_credits ? ((creditBalance.remaining / creditBalance.total_credits) * 100).toFixed(2) : 0;

  // Calculate cost for credits
  const calculateCost = (credits) => {
    if (!creditRate.data) return 0;
    return ((credits / creditRate.data.credit) * creditRate.data.cost).toFixed(2);
  };

  // Calculate amount in cents for API
  const calculateAmountInCents = (credits) => {
    if (!creditRate.data) return 0;
    return Math.round((credits / creditRate.data.credit) * creditRate.data.cost * 100);
  };

  // Calculate credits from USD
  const calculateCreditsFromUsd = (usd) => {
    if (!creditRate.data) return 0;
    return Math.round((usd / creditRate.data.cost) * creditRate.data.credit);
  };

  // Get progress bar color based on remaining percentage
  const getProgressColor = (percentage) => {
    const num = parseFloat(percentage);
    if (num <= 26) return '#ff4d4f'; // red
    if (num <= 50) return '#faad14'; // yellow
    return '#1890ff'; // blue
  };

  // Handle checkout
  const handleCheckout = (credits, packageId, amountInCents = null) => {
    if (!creditRate.data) {
      message.error('Credit rate not loaded yet. Please wait.');
      return;
    }

    if (credits < 50) {
      message.warning('Minimum purchase is 50 credits');
      return;
    }

    const amount = amountInCents || calculateAmountInCents(credits);
    
    setCheckoutLoading(packageId);

    const payload = {
      credits: credits,
      amount: amount,
      success_url: `${window.location.origin}/settings?tab=billing-usage#success`,
      cancel_url: `${window.location.origin}/settings?tab=billing-usage#cancel`,
    };

    dispatch(createCheckoutSessionApi({
      data: payload,
      successCallback: (response) => {
        if (response?.checkout_url) {
          message.success('Redirecting to checkout...');
          window.location.href = response.checkout_url;
        } else {
          message.error('Failed to create checkout session');
        }
      },
      failureCallback: (error) => {
        console.error('Checkout Error:', error);
        message.error('Failed to create checkout session. Please try again.');
      },
      finalCallback: () => {
        setCheckoutLoading(null);
      },
    }));
  };

  const fetchTransactions = (page = meta.page, limit = meta.limit) => {
    setLoading((prev) => ({ ...prev, transactions: true }));

    const params = {
      transaction_type: transactionType,
      limit: limit,
      page: page,
    };

    if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
      params.start_date = dayjs(dateRange[0]).format('YYYY-MM-DD');
      params.end_date = dayjs(dateRange[1]).format('YYYY-MM-DD');
    }

    dispatch(getCreditHistoryApi({
      params,
      successCallback: (response) => {
        setCreditHistoryTransactions(response?.transactions || []);
        setMeta({
          total: response?.total || 0,
          page: response?.page || page,
          limit: response?.limit || limit,
        });

        const counts = {
          addition: response?.total_cost?.addition || 0,
          usage: response?.total_cost?.usage || 0,  
        };

        setTransactionCounts(counts);
      },
      failureCallback: (errors) => {
        console.error('API Error: ', errors);
        message.error('Failed to fetch credit history.');
      },
      finalCallback: () => {
        setLoading((prev) => ({ ...prev, transactions: false }));
      },
    }));
  };

  const handleTransactionTypeChange = (value) => {
    setTransactionType(value);
    setMeta((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setMeta((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setTransactionType(null);
    setDateRange(null);
    setMeta((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    fetchTransactions(meta.page, meta.limit);
  }, [dispatch, transactionType, dateRange]);

  useEffect(() => {
  (() => {
    dispatch(getCreditRateApi({
      successCallback: (response) => {
        setCreditRate({
          loading: false,
          data: {
            credit: response?.credit,
            cost: response?.cost,
            currency: response?.currency,
          },
          packages: response?.package || [],
        });
      }
      ,
      failureCallback: (errors) => {
        console.error('API Error: ', errors);
        message.error('Failed to fetch credit rate.');
      },
      finalCallback: () => {}
    }));
  })();
  }, [dispatch]);

  console.log("Credit rate", creditRate);
  

  useEffect(() => {
    setLoading((prev) => ({ ...prev, balance: true }));
    dispatch(getCreditBalanceApi({
      successCallback: (response) => {
        setCreditBalance({
          total_credits: response?.balance?.total_credits || 0,
          remaining: response?.balance?.remaining || 0,
        });
      },
      failureCallback: (errors) => {
        console.error('API Error: ', errors);
        message.error('Failed to fetch credit balance.');
      },
      finalCallback: () => {
        setLoading((prev) => ({ ...prev, balance: false }));
      },
    }));
  }, [dispatch]);

  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (text) => <>{formatDate(text)}</>,
    },
    {
      title: 'Transaction Type',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      render: (text) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {text[0].toUpperCase() + text.slice(1)}
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text, record) => {
        const isAddition = record.transaction_type === 'addition';
        const color = isAddition ? 'green' : 'red';
        const tooltipText = isAddition ? 'Credit Added' : 'Credit Used';

        return (
          <Tooltip title={tooltipText} placement="topLeft">
            <div style={{ color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {text} credits
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</div>
      ),
    },
  ];

  const handlePaginationChange = (page, pageSize) => {
    setMeta((prev) => ({ ...prev, page, limit: pageSize }));
    fetchTransactions(page, pageSize);
  };

  // Handle payment result from URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#success' || hash === '#cancel') {
      setPaymentDialog({
        visible: true,
        status: hash === '#success' ? 'success' : 'cancel',
      });
      // Remove hash from URL without page reload
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  // Close payment dialog
  const handleClosePaymentDialog = () => {
    setPaymentDialog({
      visible: false,
      status: null,
    });
  };

  return (
    <>
      <style>
        {`
          .shadow-card {
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            border-radius: 12px;
          }
          .info-card {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border: 1px solid #e2e8f0;
            height: 100%;
            border-radius: 12px;
            transition: all 0.3s ease;
          }
          .info-card:hover {
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
          }
          .list-disc {
            list-style-type: disc;
            padding-left: 20px;
          }
          .list-circle {
            list-style-type: circle;
            padding-left: 20px;
            margin-top: 4px;
          }
          .card-title {
            color: #2d3748;
            margin-bottom: 16px;
            font-weight: 600;
          }
          .card-text {
            color: #718096;
          }
          .equal-height-row {
            display: flex;
            flex-wrap: wrap;
          }
          .equal-height-col {
            display: flex;
            flex-direction: column;
          }
          .equal-height-card {
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          .stats-card {
            background-color: #f9f9fa;
            border: 1px solid #e2e8f0;
            margin-bottom: 24px;
            padding: 16px;
            border-radius: 8px;
          }
          .purchase-card {
            background: #fff;
            border: 2px solid #e2e8f0;
            border-radius: 16px;
            transition: all 0.3s ease;
            cursor: pointer;
            position: relative;
            overflow: hidden;
          }
          .purchase-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.15);
          }
          .purchase-card.popular {
            border-color: #1890ff;
            background: linear-gradient(180deg, #e6f7ff 0%, #fff 30%);
          }
          .purchase-card-header {
            text-align: center;
            padding: 24px 16px 16px;
          }
          .purchase-card-icon {
            font-size: 32px;
            margin-bottom: 12px;
          }
          .purchase-card-credits {
            font-size: 28px;
            font-weight: 700;
            color: #2d3748;
            margin: 0;
          }
          .purchase-card-label {
            font-size: 14px;
            color: #718096;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-top: 4px;
          }
          .purchase-card-price {
            text-align: center;
            padding: 16px;
            background: #f8fafc;
            border-top: 1px solid #e2e8f0;
          }
          .purchase-card-amount {
            font-size: 24px;
            font-weight: 600;
            color: #2d3748;
          }
          .purchase-card-currency {
            font-size: 14px;
            color: #718096;
          }
          .purchase-card-action {
            padding: 16px;
            text-align: center;
          }
          .popular-badge {
            position: absolute;
            top: -1px;
            right: -1px;
            background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
            color: white;
            padding: 4px 16px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            border-radius: 0 14px 0 12px;
          }
          .custom-card {
            background: linear-gradient(135deg, #f6f9fc 0%, #eef2f7 100%);
            border: 2px dashed #cbd5e0;
          }
          .custom-card:hover {
            border-color: #1890ff;
            border-style: solid;
          }
          .balance-card {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border: 1px solid #e2e8f0;
            color: #1f2937;
          }
          .balance-card .ant-statistic-title {
            color: rgba(31, 41, 55, 0.85);
          }
          .balance-card .ant-statistic-content {
            color: #111827;
          }
          .balance-card .ant-progress-text {
            color: #111827 !important;
          }
          .balance-card .ant-statistic-prefix,
          .balance-card .ant-statistic-suffix {
            color: #111827;
          }
          .section-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 24px;
          }
          .section-header-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            color: white;
          }
          .credit-input-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 12px 0;
          }
          .credit-input-wrapper .ant-input-number {
            width: 140px;
            font-size: 18px;
            font-weight: 600;
          }
          .feature-list {
            list-style: none;
            padding: 0;
            margin: 16px 0;
          }
          .feature-list li {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 0;
            color: #4a5568;
            font-size: 14px;
          }
          .feature-list li .anticon {
            color: #52c41a;
          }
          
          /* Payment Dialog Animation Styles */
          @keyframes scaleIn {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.1);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes checkmark {
            0% {
              stroke-dashoffset: 100;
            }
            100% {
              stroke-dashoffset: 0;
            }
          }
          
          @keyframes circleFill {
            0% {
              transform: scale(0);
            }
            100% {
              transform: scale(1);
            }
          }
          
          @keyframes shake {
            0%, 100% {
              transform: translateX(0);
            }
            10%, 30%, 50%, 70%, 90% {
              transform: translateX(-5px);
            }
            20%, 40%, 60%, 80% {
              transform: translateX(5px);
            }
          }
          
          @keyframes fadeInUp {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.4);
            }
            50% {
              box-shadow: 0 0 0 15px rgba(82, 196, 26, 0);
            }
          }
          
          @keyframes pulseFail {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4);
            }
            50% {
              box-shadow: 0 0 0 15px rgba(255, 77, 79, 0);
            }
          }
          
          .payment-dialog-content {
            text-align: center;
            padding: 48px 32px;
          }
          
          .payment-icon-wrapper {
            width: 120px;
            height: 120px;
            margin: 0 auto 32px;
            position: relative;
          }
          
          .payment-icon-circle {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: scaleIn 0.5s ease-out forwards;
          }
          
          .payment-icon-circle.success {
            background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
            box-shadow: 0 15px 40px rgba(82, 196, 26, 0.35);
            animation: scaleIn 0.5s ease-out forwards, pulse 2s ease-in-out 0.5s infinite;
          }
          
          .payment-icon-circle.cancel {
            background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
            box-shadow: 0 15px 40px rgba(255, 77, 79, 0.35);
            animation: scaleIn 0.5s ease-out forwards, shake 0.5s ease-out 0.5s, pulseFail 2s ease-in-out 1s infinite;
          }
          
          .payment-icon-circle .anticon {
            font-size: 56px;
            color: white;
            animation: scaleIn 0.3s ease-out 0.2s forwards;
            opacity: 0;
          }
          
          .payment-dialog-title {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 16px;
            animation: fadeInUp 0.4s ease-out 0.3s forwards;
            opacity: 0;
          }
          
          .payment-dialog-title.success {
            color: #52c41a;
          }
          
          .payment-dialog-title.cancel {
            color: #ff4d4f;
          }
          
          .payment-dialog-message {
            font-size: 16px;
            color: #666;
            margin-bottom: 32px;
            line-height: 1.6;
            animation: fadeInUp 0.4s ease-out 0.4s forwards;
            opacity: 0;
            max-width: 320px;
            margin-left: auto;
            margin-right: auto;
          }
          
          .payment-dialog-button {
            animation: fadeInUp 0.4s ease-out 0.5s forwards;
            opacity: 0;
          }
          
          .payment-dialog-button .ant-btn {
            min-width: 160px;
            height: 48px;
            font-size: 16px;
            border-radius: 24px;
            font-weight: 600;
          }
        `}
      </style>

      {/* Payment Result Dialog */}
      <Modal
        open={paymentDialog.visible}
        onCancel={handleClosePaymentDialog}
        footer={null}
        centered
        width={480}
        closable={false}
        maskClosable={true}
        styles={{
          body: { padding: 0 },
          content: { borderRadius: 24, overflow: 'hidden' }
        }}
      >
        <div className="payment-dialog-content">
          <div className="payment-icon-wrapper">
            <div className={`payment-icon-circle ${paymentDialog.status}`}>
              {paymentDialog.status === 'success' ? (
                <CheckCircleOutlined />
              ) : (
                <CloseCircleOutlined />
              )}
            </div>
          </div>
          <h2 className={`payment-dialog-title ${paymentDialog.status}`}>
            {paymentDialog.status === 'success' ? 'Payment Successful!' : 'Payment Cancelled'}
          </h2>
          <p className="payment-dialog-message">
            {paymentDialog.status === 'success'
              ? 'Your credits have been added to your account successfully. You can start using them right away!'
              : 'Your payment was cancelled. No charges were made to your account.'}
          </p>
          <div className="payment-dialog-button">
            <Button
              type="primary"
              size="large"
              onClick={handleClosePaymentDialog}
              style={{
                background: paymentDialog.status === 'success' 
                  ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)'
                  : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none',
                boxShadow: paymentDialog.status === 'success'
                  ? '0 8px 20px rgba(82, 196, 26, 0.3)'
                  : '0 8px 20px rgba(24, 144, 255, 0.3)',
              }}
            >
              {paymentDialog.status === 'success' ? 'Continue' : 'Close'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Credit Balance Information */}
      <Card style={{ marginBottom: '24px' }} className="shadow-card">
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="section-header">
            <div>
              <Title level={4} style={{ margin: 0, color: '#2d3748' }}>Credit Balance</Title>
              <Text type="secondary">Monitor your credit usage and balance</Text>
            </div>
          </div>
          <Row gutter={[24, 24]} className="equal-height-row">
            <Col xs={24} lg={14} className="equal-height-col">
              <Card className="balance-card equal-height-card" style={{ borderRadius: '16px' }}>
                <Title level={5} style={{ color: '#1f2937', marginBottom: 24 }}>
                  <FireOutlined style={{ marginRight: 8, color: '#f97316' }} />
                  Credit Overview
                </Title>
                {loading.balance ? (
                  <Skeleton active paragraph={{ rows: 3 }} />
                ) : (
                  <>
                    <Row gutter={[24, 24]}>
                      <Col xs={24} sm={8} style={{ textAlign: 'center' }}>
                        <Statistic
                          title="Total Credits"
                          value={creditBalance.total_credits}
                          valueStyle={{ color: '#111827', fontSize: '28px', fontWeight: '600' }}
                          // prefix={<iconify-icon icon="bxs:coin-stack" style={{ marginRight: '8px', color: '#111827' }} />}
                        />
                      </Col>
                      <Col xs={24} sm={8} style={{ textAlign: 'center' }}>
                        <Statistic
                          title="Used Credits"
                          value={usedCredits}
                          valueStyle={{ color: '#111827', fontSize: '28px', fontWeight: '600' }}
                          // prefix={<iconify-icon icon="mingcute:check-2-fill" style={{ marginRight: '8px' }} />}
                        />
                      </Col>
                      <Col xs={24} sm={8} style={{ textAlign: 'center' }}>
                        <Statistic
                          title="Remaining"
                          value={creditBalance.remaining}
                          valueStyle={{ color: '#111827', fontSize: '28px', fontWeight: '600' }}
                          // prefix={<iconify-icon icon="bxs:coin-stack" style={{ marginRight: '8px' }} />}
                        />
                      </Col>
                    </Row>
                    <div style={{ marginTop: '24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Text style={{ color: 'rgba(31,41,55,0.85)' }}>Balance Usage</Text>
                        <Text style={{ color: '#111827', fontWeight: 600 }}>{remainingPercentage}% remaining</Text>
                      </div>
                      <Progress
                        percent={remainingPercentage}
                        showInfo={false}
                        strokeColor={getProgressColor(remainingPercentage)}
                        trailColor="#f1f5f9"
                        strokeWidth={10}
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                  </>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={10} className="equal-height-col">
              <Card className="info-card equal-height-card" style={{ borderRadius: '16px' }}>
                <Title level={5} className="card-title">
                  <iconify-icon icon="ph:lightbulb-fill" style={{ marginRight: '8px', color: '#faad14' }} />
                  How Credits Work
                </Title>
                <ul className="feature-list">
                  <li>
                    <CheckCircleOutlined />
                    <span><strong>1 Credit = ${creditRate.data?.cost || '0.01'}</strong> purchase rate</span>
                  </li>
                  <li>
                    <CheckCircleOutlined />
                    <span><strong>3 credits</strong> per message sent</span>
                  </li>
                  <li>
                    <CheckCircleOutlined />
                    <span>1 credit for Social Media message</span>
                  </li>
                  <li>
                    <CheckCircleOutlined />
                    <span>2 credits for AI response</span>
                  </li>
                  <li>
                    <CheckCircleOutlined />
                    <span>3 credits for document processing</span>
                  </li>
                  <li>
                    <CheckCircleOutlined />
                    <span>Credits never expire</span>
                  </li>
                </ul>
              </Card>
            </Col>
          </Row>
        </div>
      </Card>

      {/* Purchase Credits */}
      <Card style={{ marginBottom: '24px' }} className="shadow-card">
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="section-header">
            <div>
              <Title level={4} style={{ margin: 0, color: '#2d3748' }}>Purchase Credits</Title>
              <Text type="secondary">Choose a package or enter a custom amount</Text>
            </div>
          </div>
          <Row gutter={[24, 24]} className="equal-height-row">
            {/* Preset Packages */}
            {creditRate.loading || !creditRate.packages || creditRate.packages.length === 0 ? (
              Array.from({ length: 3 }).map((_, index) => (
                <Col xs={24} sm={12} md={6} key={index} className="equal-height-col">
                  <Card className="purchase-card" bodyStyle={{ padding: 0 }}>
                    <div className="purchase-card-header">
                      <Skeleton.Avatar active size="large" shape="circle" style={{ marginBottom: 12 }} />
                      <Skeleton.Input active size="small" style={{ width: 80, marginBottom: 8 }} />
                      <Skeleton.Input active size="small" style={{ width: 100 }} />
                      <Skeleton.Input active size="small" style={{ width: 60 }} />
                    </div>
                    <div className="purchase-card-price">
                      <Skeleton.Input active size="small" style={{ width: 80 }} />
                    </div>
                    <div className="purchase-card-action">
                      <Skeleton.Button active size="large" block />
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              creditRate.packages.map((pkg, index) => (
                <Col xs={24} sm={12} md={6} key={index} className="equal-height-col">
                  <Card 
                    className={`purchase-card ${pkg.recommended ? 'popular' : ''}`}
                    bodyStyle={{ padding: 0 }}
                  >
                    {pkg.recommended && <div className="popular-badge">Most Popular</div>}
                    <div className="purchase-card-header">
                      <div className="purchase-card-icon" style={{ color: '#1890ff' }}>
                        {PACKAGE_ICONS[index] || <ThunderboltOutlined />}
                      </div>
                      <p className="purchase-card-label">{pkg.label.charAt(0).toUpperCase() + pkg.label.slice(1)} Pack</p>
                      <p className="purchase-card-credits">{pkg.credit.toLocaleString()}</p>
                      <p className="card-text">credits</p>
                    </div>
                    <div className="purchase-card-price">
                      {creditRate.loading ? (
                        <Skeleton.Input active size="small" style={{ width: 80 }} />
                      ) : (
                        <>
                          {pkg.offer && pkg.offer_amount < pkg.amount ? (
                            <>
                              <span style={{ textDecoration: 'line-through', color: '#ccc', fontSize: '18px', marginRight: '8px' }}>${pkg.amount}</span>
                              <span className="purchase-card-amount">${pkg.offer_amount}</span>
                            </>
                          ) : (
                            <span className="purchase-card-amount">${pkg.amount}</span>
                          )}
                          <span className="purchase-card-currency"> {pkg.currency?.toUpperCase() || 'USD'}</span>
                        </>
                      )}
                    </div>
                    <div className="purchase-card-action">
                      <Button
                        type="primary"
                        size="large"
                        block
                        icon={<ShoppingCartOutlined />}
                        loading={checkoutLoading === `preset-${index}`}
                        disabled={checkoutLoading !== null || creditRate.loading}
                        onClick={() => handleCheckout(pkg.credit, `preset-${index}`, Math.round((pkg.offer && pkg.offer_amount < pkg.amount ? pkg.offer_amount : pkg.amount) * 100))}
                        style={{
                          background: pkg.recommended 
                            ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)' 
                            : undefined,
                          borderColor: pkg.recommended ? '#1890ff' : undefined,
                          height: '44px',
                          fontWeight: 600,
                        }}
                      >
                        {checkoutLoading === `preset-${index}` ? 'Processing...' : 'Buy Now'}
                      </Button>
                    </div>
                  </Card>
                </Col>
              ))
            )}
            
            {/* Custom Amount */}
            <Col xs={24} sm={12} md={6} className="equal-height-col">
              <Card 
                className="purchase-card custom-card"
                bodyStyle={{ padding: 0 }}
              >
                <div className="purchase-card-header">
                  <div className="purchase-card-icon" style={{ color: '#718096' }}>
                    <EditOutlined />
                  </div>
                  <p className="purchase-card-label" style={{ marginBottom: 8 }}>Custom Amount</p>
                  <div className="credit-input-wrapper">
                    {creditRate.loading ? (
                      <Skeleton.Input active size="small" style={{ width: 100 }} />
                    ) : (
                      <p className="purchase-card-credits">{calculateCreditsFromUsd(customUsd).toLocaleString()}</p>
                    )}
                  </div>
                  <Text type="secondary">credits</Text>
                </div>
                <div className="purchase-card-price">
                  {creditRate.loading ? (
                    <Skeleton.Input active size="small" style={{ width: 80 }} />
                  ) : (
                    <>
                      <InputNumber
                        min={1}
                        max={1000000}
                        step={10}
                        value={customUsd}
                        onChange={(value) => setCustomUsd(value || 1)}
                        formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                        size="large"
                        style={{ width: '120px', fontWeight: 600 }}
                      />  
                      <span className="purchase-card-currency"> USD</span>
                    </>
                  )}
                </div>
                <div className="purchase-card-action">
                  <Button
                    type="default"
                    size="large"
                    block
                    icon={<ShoppingCartOutlined />}
                    loading={checkoutLoading === 'custom'}
                    disabled={checkoutLoading !== null || creditRate.loading || calculateCreditsFromUsd(customUsd) < 50}
                    onClick={() => {
                      const credits = calculateCreditsFromUsd(customUsd);
                      const cents = calculateAmountInCents(credits);
                      handleCheckout(credits, 'custom', cents);
                    }}
                    style={{
                      height: '44px',
                      fontWeight: 600,
                      borderColor: '#1890ff',
                      color: '#1890ff',
                    }}
                  >
                    {checkoutLoading === 'custom' ? 'Processing...' : 'Buy Custom'}
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
          
          {/* Info note */}
          <div style={{ 
            marginTop: '24px', 
            padding: '16px', 
            background: '#f0f9ff', 
            borderRadius: '8px',
            border: '1px solid #bae7ff',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <iconify-icon icon="ph:info-fill" style={{ fontSize: '20px', color: '#1890ff' }} />
            <Text style={{ color: '#4a5568' }}>
              Payments are processed securely via Stripe. Credits are added to your account immediately after successful payment.
            </Text>
          </div>
        </div>
      </Card>

      <Card className="shadow-card">
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="section-header">
            <div>
              <Title level={4} style={{ margin: 0, color: '#2d3748' }}>Transaction History</Title>
              <Text type="secondary">View your credit transactions and usage</Text>
            </div>
          </div>

          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={6}>
              <Select
                placeholder="Transaction Type"
                style={{ width: '100%' }}
                value={transactionType}
                onChange={handleTransactionTypeChange}
                allowClear
              >
                <Select.Option value="addition">Addition</Select.Option>
                <Select.Option value="usage">Usage</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={6}>
              <RangePicker
                style={{ width: '100%' }}
                onChange={handleDateRangeChange}
                value={dateRange}
              />
            </Col>
            <Col xs={24} sm={6}>
              <Button
                onClick={handleResetFilters}
                variant='outlined'
                type='primary'
                color='danger'
              >
                Reset Filters
              </Button>
            </Col>
          </Row>

          <Card
            className="stats-card"
            bordered={false}
            style={{ padding: '0', marginBottom: 16, backgroundColor: '#f9f9fa' }}
          >
            <Row justify="space-between" align="middle" style={{ fontSize: '12px', color: '#4A5568', fontWeight: '500' }}>
              <Col>
                <span style={{ color: '#38a169' }}>
                  <iconify-icon icon="ph:plus-circle-fill" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Credits Addition: {loading.transactions ? <Skeleton.Input active size="small" style={{ width: "10px", marginLeft: "8px" }} /> : transactionCounts.addition}
                </span>
              </Col>
              <Col>
                <span style={{ color: '#e53e3e' }}>
                  <iconify-icon icon="ph:minus-circle-fill" style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Credits Usage: {loading.transactions ? <Skeleton.Input active size="small" style={{ width: "10px", marginLeft: "8px" }} /> : transactionCounts.usage}
                </span>
              </Col>
              <Col>
                {/* <span style={{ color: '#718096' }}>
                  {dateRange?.[0] && dateRange?.[1]
                    ? `${dayjs(dateRange[0]).format('MMM D')} - ${dayjs(dateRange[1]).format('MMM D')}`
                    : 'All'}
                </span> */}
                  &nbsp;
              </Col>
            </Row>
          </Card>

          {loading.transactions && creditHistoryTransactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
              <Spin size="large" tip="Loading transaction history..." />
            </div>
          ) : creditHistoryTransactions.length > 0 ? (
            <>
              <div style={{ overflowX: 'auto', width: '100%', whiteSpace: 'nowrap' }}>
                <Table
                  dataSource={creditHistoryTransactions}
                  columns={columns}
                  rowKey="transaction_id"
                  pagination={false}
                  bordered
                  loading={loading.transactions}
                />
              </div>
              <div style={{ marginTop: '24px', textAlign: 'right' }}>
                <Pagination
                  current={meta.page}
                  pageSize={meta.limit}
                  total={meta.total}
                  onChange={handlePaginationChange}
                  showSizeChanger
                  showTotal={(total) => `Total ${total} items`}
                  pageSizeOptions={['10', '20', '50']}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                />
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
              <Typography.Text>No transaction history available.</Typography.Text>
            </div>
          )}
        </div>
      </Card>
    </>
  );
};

export default UsageDashboard;